// Astronomical data for the star map: positions only, no styling.
// Catalogue: Hipparcos stars to magnitude 5.5 and IAU constellation lines,
// extracted from d3-celestial (BSD-3-Clause, © Olaf Frohn). Flat arrays of [ra, dec(, mag)] in degrees.
import skyData from './skyData.json';

const RAD = Math.PI / 180;
const J2000_UNIX_DAYS = 10957.5; // 2000-01-01T12:00Z

export const SKY_RADIUS = 500;

// RA/Dec grid: declination circles every 30°, hour circles every 2h.
const GRID = [
    ...[-60, -30, 0, 30, 60].map((dec) => Array.from({ length: 73 }, (_, i) => [i * 5, dec]).flat()),
    ...Array.from({ length: 12 }, (_, h) => Array.from({ length: 33 }, (_, i) => [h * 30, i * 5 - 80]).flat()),
];

// Greenwich mean sidereal time in degrees (IAU 1982, seconds-level accuracy).
function gmstDegrees(time) {
    const days = time / 86400000 - J2000_UNIX_DAYS;
    return (((280.46061837 + 360.98564736629 * days) % 360) + 360) % 360;
}

// shortcut: J2000 catalogue without precession (~0.7° off for dates 50 years away), fine for posters.
function projector(lat, lng, time) {
    const lst = gmstDegrees(time) + lng;
    const sinLat = Math.sin(lat * RAD);
    const cosLat = Math.cos(lat * RAD);
    return (ra, dec) => {
        const hourAngle = (lst - ra) * RAD;
        const d = dec * RAD;
        const east = -Math.cos(d) * Math.sin(hourAngle);
        const north = Math.sin(d) * cosLat - Math.cos(d) * Math.cos(hourAngle) * sinLat;
        const up = Math.sin(d) * sinLat + Math.cos(d) * Math.cos(hourAngle) * cosLat;
        // Stereographic projection centred on the zenith: the horizon lands on SKY_RADIUS.
        const k = SKY_RADIUS / (1 + up);
        // Looking up at the sky: north on top, east on the left.
        return { x: -east * k, y: -north * k, up };
    };
}

function toPath(points, project, minUp) {
    let path = '';
    let pen = false;
    for (let i = 0; i < points.length; i += 2) {
        const p = project(points[i], points[i + 1]);
        // Points slightly below the horizon keep lines continuous; the renderer clips them to the disc.
        if (p.up < minUp - 0.3) {
            pen = false;
            continue;
        }
        path += `${pen ? 'L' : 'M'}${p.x.toFixed(1)} ${p.y.toFixed(1)}`;
        pen = true;
    }
    return path;
}

const starRadius = (mag) => 1.2 * 1.3 ** (5.5 - mag);

// Sky above (lat, lng) at `time` (ms since epoch, UTC), in SVG units centred on the zenith.
// `minUp` (sine of the lowest altitude) below 0 also draws stars under the horizon, outside the disc:
// -0.35 (about -20°) fills the corners of a square around it.
export function computeSky(lat, lng, time, minUp = 0) {
    const project = projector(lat, lng, time);
    let stars = '';
    for (let i = 0; i < skyData.stars.length; i += 3) {
        const p = project(skyData.stars[i], skyData.stars[i + 1]);
        if (p.up <= minUp) continue;
        const r = Math.round(starRadius(skyData.stars[i + 2]) * 10) / 10;
        // A full circle as two arcs keeps every star in a single <path>.
        stars += `M${(p.x - r).toFixed(1)} ${p.y.toFixed(1)}a${r} ${r} 0 1 0 ${2 * r} 0a${r} ${r} 0 1 0 ${-2 * r} 0`;
    }
    return {
        stars,
        constellations: skyData.lines.map((line) => toPath(line, project, minUp)).join(''),
        grid: GRID.map((line) => toPath(line, project, minUp)).join(''),
    };
}
